import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Typography, CircularProgress, Box,
  TextField, IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { NotificationManager } from "react-notifications";
import {
  GetCustCharges, SaveCustCharge, UpdateCustCharge, DeleteCustCharge,
} from "../../../services/ClientsMaintenanceService";

const emptyRow = { itemDescription: "", price: "" };

const MiscChargesDialog = ({ open, onClose, accountNumber }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState(emptyRow);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyRow);

  useEffect(() => {
    if (open && accountNumber) load();
  }, [open, accountNumber]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await GetCustCharges(accountNumber);
      setRows(res.results || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newRow.itemDescription.trim()) return NotificationManager.warning("Description is required.");
    try {
      setLoading(true);
      await SaveCustCharge({ accountNumber, itemDescription: newRow.itemDescription.trim(), price: parseFloat(newRow.price) || 0 });
      setNewRow(emptyRow);
      await load();
    } catch { NotificationManager.error("Failed to add charge."); }
    finally { setLoading(false); }
  };

  const handleEditSave = async (row) => {
    try {
      setLoading(true);
      await UpdateCustCharge({ recordid: row.recordid, accountNumber, itemDescription: editRow.itemDescription.trim(), price: parseFloat(editRow.price) || 0 });
      setEditId(null);
      await load();
    } catch { NotificationManager.error("Failed to update charge."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (recordid) => {
    try {
      setLoading(true);
      await DeleteCustCharge(recordid);
      await load();
    } catch { NotificationManager.error("Failed to delete charge."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Miscellaneous Charges — Account {accountNumber}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Price</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography color="text.secondary" variant="body2">No charges defined.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((row) =>
                    editId === row.recordid ? (
                      <TableRow key={row.recordid}>
                        <TableCell><TextField size="small" value={editRow.itemDescription} onChange={e => setEditRow(p => ({ ...p, itemDescription: e.target.value }))} fullWidth /></TableCell>
                        <TableCell><TextField size="small" type="number" value={editRow.price} onChange={e => setEditRow(p => ({ ...p, price: e.target.value }))} sx={{ width: 100 }} /></TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => handleEditSave(row)}><SaveIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => setEditId(null)}><CancelIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={row.recordid} hover>
                        <TableCell>{row.itemDescription}</TableCell>
                        <TableCell>${(row.price || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setEditId(row.recordid); setEditRow({ itemDescription: row.itemDescription, price: row.price ?? "" }); }}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.recordid)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField size="small" label="Description" value={newRow.itemDescription} onChange={e => setNewRow(p => ({ ...p, itemDescription: e.target.value }))} sx={{ flex: 1 }} />
              <TextField size="small" label="Price" type="number" value={newRow.price} onChange={e => setNewRow(p => ({ ...p, price: e.target.value }))} sx={{ width: 100 }} />
              <Button variant="contained" size="small" onClick={handleAdd} disabled={loading}>Add</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MiscChargesDialog;
